import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Card } from 'react-bootstrap';
import GenericButton from '../updated/shared/Button';
import OpenNotification from '../OpenNotification/OpenNotification';
import LoginRequiredPopup from '../updated/shared/LoginRequiredPopup';
import { decodeJWT, sendOtp, validateOtp } from '../../services/api';
import indexText from '../../assets/header-icons/indexx-logo-v3-dark.svg';
import indexTextLight from '../../assets/header-icons/indexx-logo-v3-light.svg';
import {
  getKycStatus,
  uploadDocumentDirect,
  submitKycApplication,
  getApplicationDocuments,
  saveDocumentSelection,
  deleteKycDocument,
  type PersonalInfo,
  type KycStatus,
  type ApplicationDocument,
  type DocumentSelectionPayload,
} from '../../services/kycApi';
import { Country } from 'country-state-city';
import './VerifyWithIndexx.css';

// Function to get country flag emoji from ISO code
const getCountryFlag = (isoCode: string): string => {
  if (!isoCode || isoCode.length !== 2) return '';
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const steps = [
  'Get Started',
  'Email Verification',
  'Select Document',
  'Select Country',
  'Document Upload',
  'Review & Submit',
  'Status',
];

const mapDocumentTypeForApi = (
  type: 'PASSPORT' | 'NATIONAL_ID' | 'DRIVING_LICENSE' | null
): string | undefined => {
  switch (type) {
    case 'PASSPORT':
      return 'passport';
    case 'NATIONAL_ID':
      return 'national_id';
    case 'DRIVING_LICENSE':
      return 'driving_licence';
    default:
      return undefined;
  }
};

interface DocumentState {
  file: File | null;
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  s3Key: string | null;
}

const initialDocumentState: DocumentState = {
  file: null,
  uploadStatus: 'idle',
  s3Key: null,
};

const createInitialDocuments = () => ({
  ID_FRONT: { ...initialDocumentState },
  ID_BACK: { ...initialDocumentState },
  PASSPORT: { ...initialDocumentState },
  SELFIE: { ...initialDocumentState },
  PROOF_OF_ADDRESS: { ...initialDocumentState },
});

const humanFileSize = (size: number) => {
  if (!size) {
    return '0 KB';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

const formatDocumentTimestamp = (timestamp?: string) => {
  if (!timestamp) {
    return 'Unknown time';
  }
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown time';
  }
  return parsed.toLocaleString();
};

const formatKycDocumentLabel = (type: string) => {
  return type
    .toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getAuthenticatedEmail = (): string => {
  const token = localStorage.getItem('access_token');
  const decoded = token ? decodeJWT(token) : null;
  return typeof decoded?.email === 'string'
    ? decoded.email.trim().toLowerCase()
    : '';
};

const VerifyWithIndexx: React.FC = () => {
  const navigate = useNavigate();
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // Simple authentication check
  const checkAuthentication = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return !!(token && token !== 'null' && token !== 'undefined' && token.trim() !== '');
  }, []);

  const showLoginRequiredPopup = () => {
    setShowLoginPopup(true);
  };

  const handleLoginPopupClose = () => {
    setShowLoginPopup(false);
  };
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [kycStatus, setKycStatus] = useState<KycStatus | null>(null);
  const [shouldPollStatus, setShouldPollStatus] = useState(true);

  // Apply theme class based on userType (CaptainBee = yellow, Indexx Exchange = green)
  useEffect(() => {
    const applyTheme = () => {
      const userType = localStorage.getItem('userType');
      const userLogged = localStorage.getItem('userlogged');

      // Determine theme class based on userType or userLogged
      let themeClass = 'theme-normal'; // Default to green

      if (userType && userType !== 'Indexx Exchange') {
        // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
        themeClass = 'theme-captain'; // Yellow for CaptainBee
      } else if (userType === 'CaptainBee' || userLogged === 'captain') {
        themeClass = 'theme-captain'; // Yellow for CaptainBee
      } else if (userType === 'HoneyUser' || userLogged === 'honeyb') {
        themeClass = 'theme-honeyb'; // Yellow for HoneyBee
      } else if (userLogged === 'normal' || userType === 'Indexx Exchange') {
        themeClass = 'theme-normal'; // Green for normal users
      }

      // Apply theme class to document body and root
      document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      document.body.classList.add(themeClass);
      document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      document.documentElement.classList.add(themeClass);

      // Also apply to root element to ensure CSS variables are available
      const root = document.getElementById('root');
      if (root) {
        root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
        root.classList.add(themeClass);
      }
    };

    // Apply theme on mount
    applyTheme();

    // Listen for storage changes and custom events
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userType' || e.key === 'userlogged') {
        applyTheme();
      }
    };

    const handleUserTypeChange = () => {
      applyTheme();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userTypeChanged', handleUserTypeChange);

    // Poll for userType changes
    const pollInterval = setInterval(applyTheme, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userTypeChanged', handleUserTypeChange);
      clearInterval(pollInterval);
    };
  }, []);

  // Email and OTP State
  const [email, setEmail] = useState(getAuthenticatedEmail);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState<string[]>(new Array(6).fill(''));
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  // Document Type Selection
  const [selectedDocumentType, setSelectedDocumentType] = useState<'PASSPORT' | 'NATIONAL_ID' | 'DRIVING_LICENSE' | null>(null);
  const [showSupportedDocuments, setShowSupportedDocuments] = useState(false);

  // Country Selection
  const [selectedCountry, setSelectedCountry] = useState<string>('');

  // Personal Information Form State
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    nationality: '',
  });

  // Document Upload State
  const [documents, setDocuments] = useState(createInitialDocuments());

  // Store image URLs to prevent recreation on every render
  const [imageUrls, setImageUrls] = useState<{
    PASSPORT: string | null;
    SELFIE: string | null;
    ID_FRONT: string | null;
    ID_BACK: string | null;
  }>({
    PASSPORT: null,
    SELFIE: null,
    ID_FRONT: null,
    ID_BACK: null,
  });

  // Store fetched documents from API
  const [fetchedDocuments, setFetchedDocuments] = useState<ApplicationDocument[]>([]);
  const [deletingDocumentIds, setDeletingDocumentIds] = useState<Record<string, boolean>>({});

  // Camera state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraType, setCameraType] = useState<'ID_FRONT' | 'ID_BACK' | 'PASSPORT' | 'SELFIE' | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [pendingSelectionPayload, setPendingSelectionPayload] = useState<DocumentSelectionPayload | null>(null);
  const lastSelectionPayloadRef = useRef<string | null>(null);
  // Check authentication on mount
  useEffect(() => {
    if (!checkAuthentication()) {
      showLoginRequiredPopup();
    }
  }, [checkAuthentication, showLoginRequiredPopup]);

  // Load existing KYC status on mount (only if status endpoint exists)
  // Note: If status endpoint doesn't exist, we'll rely on applicationId from document uploads
  useEffect(() => {
    if (checkAuthentication()) {
      // Try to load status, but don't fail if endpoint doesn't exist
      loadKycStatus().catch((error) => {
        // If status endpoint doesn't exist (404 or other errors), that's okay
        // We'll get applicationId from document uploads instead
        if (error.response?.status !== 404) {
          console.log('Status endpoint not available, will use applicationId from uploads');
        }
      });
    }
  }, [checkAuthentication]);

  const handleReapply = () => {
    setActiveStep(0);
    setSelectedDocumentType(null);
    setSelectedCountry('');
    setDocuments(createInitialDocuments());
    setImageUrls({
      PASSPORT: null,
      SELFIE: null,
      ID_FRONT: null,
      ID_BACK: null,
    });
    setFetchedDocuments([]);
    setApplicationId(null);
    setKycStatus(null);
    setShouldPollStatus(true);
  };

  const attemptSaveDocumentSelection = useCallback(
    async (payload?: DocumentSelectionPayload | null) => {
      if (!payload || (!payload.documentType && !payload.country)) {
        return;
      }

      const normalizedPayload: DocumentSelectionPayload = { ...payload };

      if (!applicationId) {
        setPendingSelectionPayload(normalizedPayload);
        return;
      }

      const payloadKey = JSON.stringify({ ...normalizedPayload, applicationId });
      if (lastSelectionPayloadRef.current === payloadKey) {
        return;
      }

      try {
        await saveDocumentSelection(applicationId, normalizedPayload);
        lastSelectionPayloadRef.current = payloadKey;
        setPendingSelectionPayload(null);
      } catch (error) {
        console.error('Failed to save document selection:', error);
      }
    },
    [applicationId]
  );

  useEffect(() => {
    if (
      applicationId &&
      pendingSelectionPayload &&
      (selectedDocumentType === 'NATIONAL_ID' || selectedDocumentType === 'DRIVING_LICENSE')
    ) {
      attemptSaveDocumentSelection(pendingSelectionPayload);
    }
  }, [applicationId, pendingSelectionPayload, selectedDocumentType, attemptSaveDocumentSelection]);

  useEffect(() => {
    if (
      selectedDocumentType !== 'NATIONAL_ID' &&
      selectedDocumentType !== 'DRIVING_LICENSE'
    ) {
      setPendingSelectionPayload(null);
      lastSelectionPayloadRef.current = null;
      return;
    }

    const payload: DocumentSelectionPayload = {
      documentType: mapDocumentTypeForApi(selectedDocumentType),
    };

    if (selectedCountry) {
      payload.country = selectedCountry;
    }

    attemptSaveDocumentSelection(payload);
  }, [selectedDocumentType, selectedCountry, attemptSaveDocumentSelection]);

  // Update video element when camera stream changes
  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Auto-advance to review when passport and selfie are both uploaded
  useEffect(() => {
    if (selectedDocumentType === 'PASSPORT' &&
      documents.PASSPORT.s3Key &&
      documents.SELFIE.s3Key &&
      activeStep === 4) {
      setActiveStep(5);
    }
  }, [documents.PASSPORT.s3Key, documents.SELFIE.s3Key, selectedDocumentType, activeStep]);

  // Auto-advance to review when all documents (front, back, selfie) are uploaded for National ID or Driving License
  useEffect(() => {
    if ((selectedDocumentType === 'NATIONAL_ID' || selectedDocumentType === 'DRIVING_LICENSE') &&
      documents.ID_FRONT.s3Key &&
      documents.ID_BACK.s3Key &&
      documents.SELFIE.s3Key &&
      activeStep === 4) {
      setActiveStep(5);
    }
  }, [documents.ID_FRONT.s3Key, documents.ID_BACK.s3Key, documents.SELFIE.s3Key, selectedDocumentType, activeStep]);

  // Load documents when navigating to review step
  useEffect(() => {
    if (activeStep === 5 && applicationId) {
      loadApplicationDocuments(applicationId);
    }
  }, [activeStep, applicationId]);

  // Poll for KYC status updates when on status page and not yet approved
  useEffect(() => {
    const shouldPoll =
      activeStep === 6 &&
      applicationId &&
      shouldPollStatus &&
      kycStatus?.status &&
      kycStatus.status !== 'approved' &&
      kycStatus.status !== 'rejected';

    if (!shouldPoll) return;

    // Poll every 30 seconds to check if admin has approved
    const pollInterval = setInterval(async () => {
      try {
        const response = await getKycStatus();
        const status = (response as any).data?.data || (response as any).data;
        if (status && status.status) {
          setKycStatus(status);
          setShouldPollStatus(status.status !== 'rejected');

          // If approved, trigger user data refresh to update isKYCPass
          if (status.status === 'approved') {
            window.dispatchEvent(new Event('kycStatusUpdated'));
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        // Silently fail - status endpoint might not exist
        console.log('Status polling failed, endpoint may not exist');
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [activeStep, applicationId, kycStatus?.status, shouldPollStatus]);

  const resetDocumentsForCurrentType = () => {
    setDocuments((prev) => {
      const next = { ...prev };
      if (selectedDocumentType === 'PASSPORT') {
        next.PASSPORT = { ...initialDocumentState };
        next.SELFIE = { ...initialDocumentState };
      } else if (selectedDocumentType === 'NATIONAL_ID' || selectedDocumentType === 'DRIVING_LICENSE') {
        next.ID_FRONT = { ...initialDocumentState };
        next.ID_BACK = { ...initialDocumentState };
        next.SELFIE = { ...initialDocumentState };
      }
      return next;
    });

    setImageUrls((prev) => {
      const next = { ...prev };
      if (selectedDocumentType === 'PASSPORT') {
        next.PASSPORT = null;
        next.SELFIE = null;
      } else if (selectedDocumentType === 'NATIONAL_ID' || selectedDocumentType === 'DRIVING_LICENSE') {
        next.ID_FRONT = null;
        next.ID_BACK = null;
        next.SELFIE = null;
      }
      return next;
    });
  };

  const loadKycStatus = async () => {
    try {
      // Check if token exists before making request
      const token = localStorage.getItem('access_token');
      if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
        console.error('❌ No valid access token found');
        OpenNotification('error', 'Please log in to continue with KYC verification.');
        showLoginRequiredPopup();
        return;
      }

      const response = await getKycStatus();
      const status = (response as any).data?.data || (response as any).data;
      setKycStatus(status);
      setShouldPollStatus(status.status !== 'rejected');

      // If application exists and is submitted, go to status step
      if (status.applicationId) {
        setApplicationId(status.applicationId);
        // Load documents when we have applicationId
        loadApplicationDocuments(status.applicationId);
        if (status.status !== 'draft') {
          setActiveStep(6); // Go to status step (step 6 now)

          // If approved, trigger user data refresh to update isKYCPass in account page
          if (status.status === 'approved') {
            window.dispatchEvent(new Event('kycStatusUpdated'));
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to load KYC status:', error);

      // Handle 401 Unauthorized - token expired or invalid
      if (error.response?.status === 401 || error.status === 401) {
        console.error('❌ Authentication failed - token may be expired');
        OpenNotification('error', 'Your session has expired. Please log in again.');
        // Clear invalid token
        localStorage.removeItem('access_token');
        showLoginRequiredPopup();
        return;
      }

      // 404 or no application is normal - user can start fresh
      // Don't show error notification for 404, it's expected when no application exists
      if (error.response?.status !== 404) {
        console.error('Unexpected error loading KYC status:', error);
      }
    }
  };

  const loadApplicationDocuments = async (appId: string) => {
    try {
      const response = await getApplicationDocuments(appId);
      if (response.data?.documents) {
        setFetchedDocuments(response.data.documents);
        console.log('Loaded application documents:', response.data.documents);
      }
    } catch (error: any) {
      // 404 is normal when no documents exist yet
      if (error.response?.status !== 404) {
        console.error('Failed to load application documents:', error);
      }
    }
  };

  const handleDeleteKycDocument = async (doc: ApplicationDocument) => {
    if (!applicationId) {
      OpenNotification('error', 'No application found to delete this document.');
      return;
    }

    if (deletingDocumentIds[doc._id]) {
      return;
    }

    setDeletingDocumentIds((prev) => ({ ...prev, [doc._id]: true }));
    try {
      await deleteKycDocument(applicationId, doc._id);
      const label = formatKycDocumentLabel(doc.type);
      OpenNotification('success', `${label} removed successfully`);
      await loadApplicationDocuments(applicationId);
    } catch (error: any) {
      console.error('Failed to delete KYC document:', error);
      const errorMessage = error?.response?.data?.message || error.message || 'Failed to delete document';
      OpenNotification('error', errorMessage);
    } finally {
      setDeletingDocumentIds((prev) => {
        const next = { ...prev };
        delete next[doc._id];
        return next;
      });
    }
  };

  const handlePersonalInfoChange = (field: keyof PersonalInfo, value: string) => {
    setPersonalInfo((prev) => ({ ...prev, [field]: value }));
  };

  const validatePersonalInfo = (): boolean => {
    const required = ['firstName', 'lastName', 'dateOfBirth', 'address', 'city', 'country', 'postalCode', 'nationality'];
    for (const field of required) {
      if (!personalInfo[field as keyof PersonalInfo]) {
        OpenNotification('error', `Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    return true;
  };

  const handleStart = () => {
    setActiveStep(1); // Move to email verification step
  };

  const handleEmailSubmit = async () => {
    const authenticatedEmail = getAuthenticatedEmail();
    if (!authenticatedEmail) {
      OpenNotification('error', 'Unable to identify your account email. Please log in again.');
      showLoginRequiredPopup();
      return;
    }

    if (email.trim().toLowerCase() !== authenticatedEmail) {
      setEmail(authenticatedEmail);
      OpenNotification('error', 'Email must match the currently logged-in account');
      return;
    }

    setLoading(true);
    try {
      const res = await sendOtp(authenticatedEmail);
      if (res.status === 200) {
        const responseMessage =
          typeof res.data === 'string'
            ? res.data.trim().toLowerCase()
            : '';

        // The sendOtp endpoint returns 200 without sending a code when the
        // account's email has already been verified. In that case, skip the
        // OTP form so the user can continue the KYC flow.
        if (responseMessage.includes('email already verified')) {
          setEmailVerified(true);
          setOtpSent(false);
          setOtpCode(new Array(6).fill(''));
          setActiveStep(2);
          OpenNotification('success', 'Email is already verified');
          return;
        }

        setOtpSent(true);
        OpenNotification('success', 'Verification code sent to your email');
      } else {
        OpenNotification('error', res.data || 'Failed to send verification code');
      }
    } catch (error: any) {
      console.error('Failed to send OTP:', error);
      OpenNotification('error', error.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpInput = (index: number, value: string) => {
    // Only allow numeric values
    if (value && !/^\d$/.test(value)) return;
    if (value.length > 1) return;

    const newOtpCode = [...otpCode];
    newOtpCode[index] = value;
    setOtpCode(newOtpCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtpCode = pastedData.split('').slice(0, 6);
      setOtpCode([...newOtpCode, ...new Array(6 - newOtpCode.length).fill('')]);
    }
  };

  const maskEmail = (email: string) => {
    if (!email || !email.includes('@')) return email;
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 1) return email;
    const firstChar = localPart[0];
    const masked = firstChar + '*'.repeat(Math.min(localPart.length - 1, 10));
    return `${masked}@${domain}`;
  };

  const handleOtpVerify = async () => {
    const authenticatedEmail = getAuthenticatedEmail();
    if (!authenticatedEmail || email.trim().toLowerCase() !== authenticatedEmail) {
      setEmail(authenticatedEmail);
      OpenNotification('error', 'Email must match the currently logged-in account');
      return;
    }

    const code = otpCode.join('');
    if (code.length !== 6) {
      OpenNotification('error', 'Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const res = await validateOtp(authenticatedEmail, code);
      if (res.status === 200) {
        OpenNotification('success', 'Email verified successfully');
        setEmailVerified(true);
        setActiveStep(2); // Move to document type selection step
      } else {
        OpenNotification('error', res.data || 'Invalid verification code');
        setOtpCode(new Array(6).fill(''));
      }
    } catch (error: any) {
      console.error('Failed to verify OTP:', error);
      OpenNotification('error', error.message || 'Failed to verify code');
      setOtpCode(new Array(6).fill(''));
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentTypeSelect = (type: 'PASSPORT' | 'NATIONAL_ID' | 'DRIVING_LICENSE') => {
    setSelectedDocumentType(type);
    setSelectedCountry('');
    if (type === 'PASSPORT') {
      setActiveStep(4); // Skip country selection for passport, go directly to document upload
    } else {
      setActiveStep(3); // Move to country selection step for National ID and Driving License
    }
  };

  const submitApplication = async () => {
    let applicationIdToSubmit = applicationId;

    // If we don't have applicationId, try to get it from uploaded documents
    if (!applicationIdToSubmit) {
      // Check if any document has been uploaded - applicationId should be stored from upload response
      const uploadedDoc = Object.values(documents).find(doc => doc.s3Key);
      if (!uploadedDoc) {
        OpenNotification('error', 'Please upload at least one document before submitting.');
        return;
      }

      // Try to get applicationId from the most recent upload
      // The applicationId should have been set during document upload
      if (!applicationId) {
        OpenNotification('error', 'Application ID not found. Please upload a document first or contact support.');
        return;
      }
      applicationIdToSubmit = applicationId;
    }

    if (!applicationIdToSubmit) {
      OpenNotification('error', 'No KYC application found. Please upload a document first.');
      return;
    }

    setLoading(true);
    try {
      const submitResponse: any = await submitKycApplication(applicationIdToSubmit);
      OpenNotification('success', 'Application submitted successfully!');

      // Update status from submit response if available
      if (submitResponse.data?.data) {
        const submittedApp = submitResponse.data.data;
        const newStatus = submittedApp.status || 'pending';
        setKycStatus({
          status: newStatus,
          applicationId: applicationIdToSubmit,
        });

        // If approved, trigger user data refresh to update isKYCPass
        if (newStatus === 'approved') {
          window.dispatchEvent(new Event('kycStatusUpdated'));
        }
      }

      setActiveStep((prev) => prev + 1);
    } catch (error: any) {
      console.error('Failed to submit application:', error);
      const message = error?.data?.message || error?.message || 'Failed to submit application';
      OpenNotification('error', message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 3) {
      // Country selection step - validate country is selected
      if (!selectedCountry) {
        OpenNotification('error', 'Please select a country');
        return;
      }
      setActiveStep(4); // Move to document upload step
      return;
    }

    if (activeStep === 4) {
      // Check required documents based on selected document type
      let hasRequiredDocs = false;
      let errorMessage = '';

      if (selectedDocumentType === 'PASSPORT') {
        hasRequiredDocs = !!documents.PASSPORT.s3Key && !!documents.SELFIE.s3Key;
        errorMessage = 'Please upload Passport Front and Selfie';
      } else if (selectedDocumentType === 'NATIONAL_ID') {
        hasRequiredDocs = !!documents.ID_FRONT.s3Key && !!documents.ID_BACK.s3Key && !!documents.SELFIE.s3Key;
        errorMessage = 'Please upload National ID Front, Back, and Selfie';
      } else if (selectedDocumentType === 'DRIVING_LICENSE') {
        hasRequiredDocs = !!documents.ID_FRONT.s3Key && !!documents.ID_BACK.s3Key && !!documents.SELFIE.s3Key;
        errorMessage = 'Please upload Driving License Front, Back, and Selfie';
      }

      if (!hasRequiredDocs) {
        OpenNotification('error', errorMessage);
        return;
      }

      // Ensure applicationId exists before proceeding
      if (!applicationId) {
        // Try to load from status
        try {
          const statusResponse = await getKycStatus();
          if (statusResponse.data.applicationId) {
            setApplicationId(statusResponse.data.applicationId);
          } else {
            OpenNotification('error', 'No KYC application found. Please contact support.');
            return;
          }
        } catch (error: any) {
          OpenNotification('error', 'Failed to load KYC application. Please try again.');
          return;
        }
      }
      setActiveStep((prev) => prev + 1);
    } else if (activeStep === 5) {
      await submitApplication();
    }
  };

  const handleBack = () => {
    if (activeStep === 5) {
      // If going back from Review & Submit, clear documents
      resetDocumentsForCurrentType();
      setActiveStep(4);
      return;
    }

    if (activeStep === 4) {
      // If going back from document upload step
      if (selectedDocumentType === 'PASSPORT') {
        // Passport skips country selection, so go back to document type selection (step 2)
        setActiveStep(2);
        return;
      } else if (selectedDocumentType === 'NATIONAL_ID' || selectedDocumentType === 'DRIVING_LICENSE') {
        // National ID and Driving License go through country selection, so go back to country selection (step 3)
        setActiveStep(3);
        return;
      }
    }

    if (activeStep === 3) {
      // If going back from country selection, clear country and go back to document type selection
      setSelectedCountry('');
      setActiveStep(2);
      return;
    }

    if (activeStep === 1) {
      // Reset email verification state when going back from email step
      setEmail('');
      setOtpSent(false);
      setOtpCode(new Array(6).fill(''));
      setEmailVerified(false);
    }
    setActiveStep((prev) => prev - 1);
  };

  const renderKycDocumentManagementPanel = () => {
    if (activeStep < 4 || fetchedDocuments.length === 0) {
      return null;
    }

    return (
      <div className="kyc-uploaded-documents-panel">
        <div className="kyc-uploaded-documents-header">
          <h4>Uploaded documents</h4>
          <span>{fetchedDocuments.length} file{fetchedDocuments.length === 1 ? '' : 's'}</span>
        </div>
        <div className="kyc-uploaded-documents-list">
          {fetchedDocuments.map((doc) => {
            const label = formatKycDocumentLabel(doc.type);
            const metaParts = [
              doc.side ? (doc.side === 'front' ? 'Front side' : 'Back side') : '',
              formatDocumentTimestamp(doc.createdAt),
              humanFileSize(doc.fileSize),
            ].filter(Boolean);

            return (
              <div key={doc._id} className="kyc-uploaded-document-row">
                <div className="kyc-uploaded-document-info">
                  <strong>{label}</strong>
                  <span className="kyc-uploaded-document-meta">{metaParts.join(' · ')}</span>
                </div>
                <div className="kyc-document-actions">
                  <GenericButton
                    text="View"
                    onClick={() => {
                      if (doc.url) {
                        window.open(doc.url, '_blank', 'noopener');
                      } else {
                        OpenNotification('error', 'Document preview is not available.');
                      }
                    }}
                    disabled={!doc.url}
                    className="kyc-document-view-button"
                    styles={{ width: 'auto', minWidth: '100px', padding: '6px 14px' }}
                  />
                  <GenericButton
                    text="Delete"
                    onClick={() => handleDeleteKycDocument(doc)}
                    loading={!!deletingDocumentIds[doc._id]}
                    disabled={!!deletingDocumentIds[doc._id]}
                    className="kyc-document-delete-button"
                    styles={{ width: 'auto', minWidth: '120px', padding: '6px 12px' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const launchCamera = async (type: 'ID_FRONT' | 'ID_BACK' | 'PASSPORT' | 'SELFIE') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: type === 'SELFIE' ? 'user' : 'environment' }
      });
      setCameraStream(stream);
      setCameraType(type);
      setShowCamera(true);
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      OpenNotification('error', 'Unable to access camera. Please check permissions or upload a file instead.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
    setCameraType(null);
  };

  const capturePhoto = async () => {
    if (!cameraStream || !cameraType || !videoRef.current) return;

    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      OpenNotification('error', 'Camera not ready. Please wait a moment.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], `camera-${cameraType}-${Date.now()}.jpg`, { type: 'image/jpeg' });
          // Upload the captured image - handleFileChange will ensure application exists
          await handleFileChange(cameraType, file);
          stopCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const handleFileChange = async (
    type: 'ID_FRONT' | 'ID_BACK' | 'PASSPORT' | 'SELFIE' | 'PROOF_OF_ADDRESS',
    file: File | null,
    providedApplicationId?: string | null
  ) => {
    if (!file) {
      return;
    }

    // Check if token exists before uploading
    const token = localStorage.getItem('access_token');
    if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
      console.error('❌ No valid access token found');
      OpenNotification('error', 'Please log in to upload documents.');
      showLoginRequiredPopup();
      return;
    }

    // Backend creates application automatically, so we don't need applicationId before upload
    // We'll get it from the upload response

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      OpenNotification('error', 'File size must be less than 10MB');
      return;
    }

    const allowedExtensions = ['jpeg', 'jpg', 'png', 'pdf'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      OpenNotification('error', 'File must be JPEG, PNG, or PDF');
      return;
    }

    // Update document state
    setDocuments((prev) => ({
      ...prev,
      [type]: { ...prev[type], file, uploadStatus: 'uploading' },
    }));

    try {
      // Determine side based on document type
      let side: 'front' | 'back' | undefined = undefined;
      if (type === 'ID_BACK') {
        side = 'back';
      } else if (type === 'ID_FRONT' || type === 'PASSPORT') {
        side = 'front';
      }

      // Upload directly to backend (backend handles S3 upload and creates application if needed)
      // Backend route is /documents/upload and doesn't require applicationId
      const uploadResponse: any = await uploadDocumentDirect(
        type,
        file,
        selectedDocumentType || null,
        side,
        selectedCountry || undefined
      );
      const uploadPayload = uploadResponse?.data?.data || uploadResponse?.data || {};
      const documentData = uploadPayload.document || uploadResponse?.data?.document;
      const s3Key =
        documentData?.s3Key ||
        uploadPayload?.s3Key ||
        uploadResponse?.data?.s3Key ||
        `${type}-${Date.now()}`;

      // If we got applicationId from the upload response, save it and load documents
      if (uploadPayload?.applicationId) {
        setApplicationId(uploadPayload.applicationId);
        loadApplicationDocuments(uploadPayload.applicationId);
      } else if (uploadResponse?.data?.applicationId) {
        setApplicationId(uploadResponse.data.applicationId);
        loadApplicationDocuments(uploadResponse.data.applicationId);
      }

      // Update document state - preserve the file object for image preview
      const updatedDocuments = {
        ...documents,
        [type]: { ...documents[type], file, uploadStatus: 'success', s3Key },
      };

      setDocuments(updatedDocuments);

      // Create and store image URL for preview
      if (file && (type === 'PASSPORT' || type === 'SELFIE' || type === 'ID_FRONT' || type === 'ID_BACK')) {
        const imageUrl = URL.createObjectURL(file);
        setImageUrls((prev) => ({
          ...prev,
          [type]: imageUrl,
        }));
      }

      // Auto-advance to review page after selfie is captured (for passport flow)
      if (type === 'SELFIE' && selectedDocumentType === 'PASSPORT' && updatedDocuments.PASSPORT.s3Key) {
        // Immediately move to review step after selfie upload
        setTimeout(() => {
          setActiveStep(5); // Move to Review & Submit step (step 5)
        }, 500);
      }

      // Auto-advance to review page after all required documents are uploaded (for ID flows)
      if (selectedDocumentType === 'NATIONAL_ID' || selectedDocumentType === 'DRIVING_LICENSE') {
        const hasFront = updatedDocuments.ID_FRONT.s3Key;
        const hasBack = updatedDocuments.ID_BACK.s3Key;
        const hasSelfie = updatedDocuments.SELFIE.s3Key;

        // If all three documents (front, back, selfie) are uploaded, move to review
        if (hasFront && hasBack && hasSelfie) {
          setTimeout(() => {
            setActiveStep(5); // Move to Review & Submit step (step 5)
          }, 500);
        }
      }

      OpenNotification('success', `${type.replace(/_/g, ' ')} uploaded successfully`);

      // Auto-advance to selfie capture after passport/ID front is uploaded
      if ((type === 'PASSPORT' && selectedDocumentType === 'PASSPORT') ||
        (type === 'ID_FRONT' && (selectedDocumentType === 'NATIONAL_ID' || selectedDocumentType === 'DRIVING_LICENSE'))) {
        // Wait a moment for UI to update, then the UI will automatically show selfie option
        // The getDocumentTypes function will show selfie after the document is uploaded
        setTimeout(() => {
          // Force re-render to show selfie option
          setDocuments((prev) => ({ ...prev }));
        }, 500);
      }
    } catch (error: any) {
      console.error(`Failed to upload ${type}:`, error);

      // Handle 401 Unauthorized - token expired or invalid
      if (error.response?.status === 401 || error.status === 401) {
        console.error('❌ Authentication failed during upload - token may be expired');
        OpenNotification('error', 'Your session has expired. Please log in again.');
        // Clear invalid token
        localStorage.removeItem('access_token');
        showLoginRequiredPopup();
        setDocuments((prev) => ({
          ...prev,
          [type]: { ...prev[type], uploadStatus: 'idle' },
        }));
        return;
      }

      // Check if error is about missing application
      const errorMessage = error.response?.data?.message || error.message || `Failed to upload ${type.replace(/_/g, ' ')}`;

      // If error mentions application not found, provide helpful message
      if (errorMessage.toLowerCase().includes('application') && errorMessage.toLowerCase().includes('not found')) {
        OpenNotification('error', 'KYC application not found. Please contact support to start the KYC process.');
      } else {
        OpenNotification('error', errorMessage);
      }

      setDocuments((prev) => ({
        ...prev,
        [type]: { ...prev[type], uploadStatus: 'error' },
      }));
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div className="kyc-step-content">
            <div className="kyc-get-started">
              <div className="kyc-logo-container">
                <img
                  src={indexText}
                  alt="Indexx.ai"
                  className="kyc-logo kyc-logo-on-light"
                />
                <img
                  src={indexTextLight}
                  alt="Indexx.ai"
                  className="kyc-logo kyc-logo-on-dark"
                />
              </div>
              <h3 className="kyc-step-title">To register</h3>
              <p className="kyc-step-subtitle">
                You will be asked to provide one of these:
              </p>
              <ul className="kyc-document-list">
                <li>Passport</li>
                <li>National ID</li>
                <li>Driving License</li>
              </ul>

              <div className="kyc-start-section">
                <GenericButton
                  text="Start"
                  onClick={handleStart}
                  className="kyc-start-button"
                />
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="kyc-step-content">
            <div className="kyc-email-verification">
              {!otpSent ? (
                <div className="kyc-email-section">
                  <h3 className="kyc-step-title">Email Verification</h3>
                  <p className="kyc-step-subtitle">
                    Verification will use the email linked to your logged-in account
                  </p>
                  <TextField
                    label="Account Email"
                    type="email"
                    value={email}
                    fullWidth
                    required
                    className="kyc-form-field"
                    placeholder="Your logged-in email"
                    InputProps={{ readOnly: true }}
                    helperText="This email is linked to your logged-in account"
                  />
                  <div className="kyc-actions kyc-actions-center">
                    <GenericButton
                      text="Send Verification Code"
                      onClick={handleEmailSubmit}
                      loading={loading}
                      className="kyc-next-button"
                    />
                  </div>
                </div>
              ) : (
                <div className="kyc-otp-section">
                  <h3 className="kyc-step-title">Enter Verification Code</h3>
                  <p className="kyc-otp-subtitle">
                    We sent a 6-digit code to your email address ({maskEmail(email)}). Please check your inbox and enter the code below.
                  </p>
                  <div className="kyc-otp-container">
                    {otpCode.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-input-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpInput(index, e.target.value)}
                        onPaste={index === 0 ? handleOtpPaste : undefined}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !digit && index > 0) {
                            const prevInput = document.getElementById(`otp-input-${index - 1}`);
                            prevInput?.focus();
                          }
                        }}
                        className="kyc-otp-input"
                      />
                    ))}
                  </div>
                  <div className="kyc-actions kyc-actions-center">
                    <GenericButton
                      text="Verify Code"
                      onClick={handleOtpVerify}
                      loading={loading}
                      className="kyc-next-button"
                    />
                  </div>
                  <div className="kyc-resend-section">
                    <button
                      type="button"
                      onClick={handleEmailSubmit}
                      className="kyc-resend-link"
                      disabled={loading}
                    >
                      Resend Code
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">Register with</h3>
            <p className="kyc-step-subtitle">Select the document type you want to use for verification</p>

            <div className="kyc-document-type-selection">
              <GenericButton
                text="Passport"
                onClick={() => handleDocumentTypeSelect('PASSPORT')}
                className="kyc-document-type-button"
              />

              <div className="kyc-or-divider">
                <span>Or</span>
              </div>

              <GenericButton
                text="National ID"
                onClick={() => handleDocumentTypeSelect('NATIONAL_ID')}
                className="kyc-document-type-button"
              />

              <div className="kyc-or-divider">
                <span>Or</span>
              </div>

              <GenericButton
                text="Driving License"
                onClick={() => handleDocumentTypeSelect('DRIVING_LICENSE')}
                className="kyc-document-type-button"
              />
            </div>

            <div className="kyc-supported-documents-section">
              <button
                type="button"
                onClick={() => setShowSupportedDocuments(!showSupportedDocuments)}
                className="kyc-supported-documents-link"
              >
                {showSupportedDocuments ? 'Hide' : 'See'} the list of supported documents
              </button>

              {showSupportedDocuments && (
                <div className="kyc-supported-documents-list">
                  <h4>Supported Documents:</h4>
                  <div className="kyc-supported-documents-content">
                    <div className="kyc-doc-type-info">
                      <h5>Passport</h5>
                      <p>Valid passport from any country. Must be current and not expired.</p>
                    </div>
                    <div className="kyc-doc-type-info">
                      <h5>National ID</h5>
                      <p>Government-issued national identification card. Must be current and not expired.</p>
                    </div>
                    <div className="kyc-doc-type-info">
                      <h5>Driving License</h5>
                      <p>Valid driver's license issued by a government authority. Must be current and not expired.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        // Country Selection Step (only for National ID and Driving License)
        if (!selectedDocumentType || selectedDocumentType === 'PASSPORT') {
          return (
            <div className="kyc-step-content">
              <p>Please select a document type first.</p>
            </div>
          );
        }

        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">
              Type the name of the country issuing your {selectedDocumentType === 'NATIONAL_ID' ? 'National ID' : 'Driving License'}.
            </h3>
            <p className="kyc-step-subtitle">Country</p>

            <div className="kyc-country-selection">
              <Autocomplete
                options={Country.getAllCountries().sort((a, b) => a.name.localeCompare(b.name))}
                getOptionLabel={(option) => option.name || ''}
                value={Country.getCountryByCode(selectedCountry) || null}
                onChange={(event, newValue) => {
                  setSelectedCountry(newValue ? newValue.isoCode : '');
                }}
                clearOnEscape
                disableClearable={false}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  const searchValue = inputValue.toLowerCase().trim();
                  return options.filter((option) => {
                    const name = option.name?.toLowerCase() || '';
                    const isoCode = option.isoCode?.toLowerCase() || '';
                    // Handle USA searches
                    if (searchValue === 'usa' || searchValue === 'us' || searchValue === 'united states') {
                      return isoCode === 'us' || name.includes('united states');
                    }
                    return name.includes(searchValue) || isoCode.includes(searchValue);
                  });
                }}
                isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
                renderInput={(params) => {
                  const selectedCountryObj = Country.getCountryByCode(selectedCountry);
                  return (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Search or select country"
                      className="kyc-country-select"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selectedCountryObj ? (
                          <span style={{ marginRight: '8px', fontSize: '1.2rem' }}>
                            {getCountryFlag(selectedCountryObj.isoCode)}
                          </span>
                        ) : null,
                      }}
                    />
                  );
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.isoCode}>
                    <span className="kyc-country-option">
                      <span className="kyc-country-flag">{getCountryFlag(option.isoCode)}</span>
                      <span className="kyc-country-name">{option.name}</span>
                    </span>
                  </li>
                )}
                renderTags={() => null}
                ListboxProps={{
                  style: {
                    maxHeight: 400,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--dash-bg)',
                    color: 'var(--body_color)',
                    '& fieldset': {
                      borderColor: 'var(--border-color)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--border-color)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--primary-color, #11BE6A)',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'var(--body_color)',
                  },
                  // Style the popup icon (dropdown arrow) - make it gray, not green
                  '& .MuiAutocomplete-popupIndicator': {
                    color: 'var(--body_color)',
                    opacity: 0.7,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      opacity: 1,
                    },
                  },
                  // Style the clear indicator (X button) - make it red/error color
                  '& .MuiAutocomplete-clearIndicator': {
                    color: '#ff4444',
                    opacity: 0.7,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 68, 68, 0.1)',
                      opacity: 1,
                      color: '#ff4444',
                    },
                  },
                }}
              />
            </div>
          </div>
        );

      case 4:
        // If no document type selected, go back to selection
        if (!selectedDocumentType) {
          return (
            <div className="kyc-step-content">
              <p>Please select a document type first.</p>
            </div>
          );
        }

        // Special UI for Passport
        if (selectedDocumentType === 'PASSPORT' && !documents.PASSPORT.s3Key) {
          return (
            <div className="kyc-step-content">
              <h3 className="kyc-step-title">Passport</h3>
              <div className="kyc-passport-instructions">
                <p className="kyc-instruction-text">
                  Open the passport on the data page and take a picture.
                </p>
                <p className="kyc-instruction-text">
                  Avoid glare and make sure all 4 corners are visible.
                </p>
              </div>

              {showCamera ? (
                <div className="kyc-camera-container">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="kyc-camera-video"
                  />
                  <div className="kyc-camera-controls">
                    <GenericButton
                      text="Cancel"
                      onClick={stopCamera}
                      className="kyc-camera-cancel"
                      styles={{ backgroundColor: '#6c757d', marginRight: '10px' }}
                    />
                    <GenericButton
                      text="Capture Photo"
                      onClick={capturePhoto}
                      className="kyc-camera-capture"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="kyc-passport-screen">
                    {documents.PASSPORT.file && documents.PASSPORT.uploadStatus === 'success' ? (
                      <img
                        src={URL.createObjectURL(documents.PASSPORT.file)}
                        alt="Passport"
                        className="kyc-passport-preview"
                      />
                    ) : (
                      <div className="kyc-passport-placeholder">
                        <p>Passport preview will appear here</p>
                      </div>
                    )}
                  </div>
                  <div className="kyc-passport-actions">
                    <GenericButton
                      text="Launch Camera"
                      onClick={() => launchCamera('PASSPORT')}
                      className="kyc-camera-button"
                      styles={{
                        backgroundColor: 'var(--primary-color, #11BE6A)'
                      }}
                    />
                    <GenericButton
                      text="Upload File"
                      onClick={() => {
                        // Allow user to skip and upload file later
                        const fileInput = document.getElementById('file-PASSPORT') as HTMLInputElement;
                        if (fileInput) {
                          fileInput.click();
                        }
                      }}
                      className="kyc-not-now-button"
                      styles={{
                        backgroundColor: '#6c757d'
                      }}
                    />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      handleFileChange('PASSPORT', file);
                    }}
                    className="kyc-file-input"
                    id="file-PASSPORT"
                    style={{ display: 'none' }}
                  />
                </>
              )}
            </div>
          );
        }

        // Special UI for Selfie (after passport is uploaded)
        if (selectedDocumentType === 'PASSPORT' && documents.PASSPORT.s3Key && !documents.SELFIE.s3Key) {
          return (
            <div className="kyc-step-content">
              <h3 className="kyc-step-title">Selfie</h3>
              <div className="kyc-passport-instructions">
                <p className="kyc-instruction-text">
                  Take a selfie to verify your identity.
                </p>
                <p className="kyc-instruction-text">
                  Make sure your face is clearly visible and well-lit.
                </p>
              </div>

              {showCamera ? (
                <div className="kyc-camera-container">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="kyc-camera-video"
                  />
                  <div className="kyc-camera-controls">
                    <GenericButton
                      text="Cancel"
                      onClick={stopCamera}
                      className="kyc-camera-cancel"
                      styles={{ backgroundColor: '#6c757d', marginRight: '10px' }}
                    />
                    <GenericButton
                      text="Capture Photo"
                      onClick={capturePhoto}
                      className="kyc-camera-capture"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="kyc-passport-screen">
                    {documents.SELFIE.file && documents.SELFIE.uploadStatus === 'success' ? (
                      <img
                        src={URL.createObjectURL(documents.SELFIE.file)}
                        alt="Selfie"
                        className="kyc-passport-preview"
                      />
                    ) : (
                      <div className="kyc-passport-placeholder">
                        <p>Selfie preview will appear here</p>
                      </div>
                    )}
                  </div>
                  <div className="kyc-passport-actions">
                    <GenericButton
                      text="Launch Camera"
                      onClick={() => launchCamera('SELFIE')}
                      className="kyc-camera-button"
                      styles={{
                        backgroundColor: 'var(--primary-color, #11BE6A)'
                      }}
                    />
                    {/* <GenericButton
                      text="Not Now"
                      onClick={() => {
                        // Allow user to skip and upload file later
                        const fileInput = document.getElementById('file-SELFIE') as HTMLInputElement;
                        if (fileInput) {
                          fileInput.click();
                        }
                      }}
                      className="kyc-not-now-button"
                      styles={{ 
                        backgroundColor: '#6c757d'
                      }}
                    /> */}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      handleFileChange('SELFIE', file);
                    }}
                    className="kyc-file-input"
                    id="file-SELFIE"
                    style={{ display: 'none' }}
                  />
                </>
              )}
            </div>
          );
        }

        // If passport and selfie are both uploaded, don't show document upload - should be on review page
        // This is a safety check in case auto-advance didn't work
        if (selectedDocumentType === 'PASSPORT' && documents.PASSPORT.s3Key && documents.SELFIE.s3Key) {
          // This should not happen as we auto-advance, but if it does, show empty state
          // The auto-advance in handleFileChange should have already moved to step 5
          return (
            <div className="kyc-step-content">
              <p>All required documents uploaded. Please proceed to review.</p>
            </div>
          );
        }

        // Handle National ID and Driving License - sequential flow (Front -> Back -> Selfie)
        if (selectedDocumentType === 'NATIONAL_ID' || selectedDocumentType === 'DRIVING_LICENSE') {
          const docTypeName = selectedDocumentType === 'NATIONAL_ID' ? 'National ID' : 'Driving License';
          const hasFront = documents.ID_FRONT.s3Key;
          const hasBack = documents.ID_BACK.s3Key;
          const hasSelfie = documents.SELFIE.s3Key;

          // Show Front capture screen
          if (!hasFront) {
            return (
              <div className="kyc-step-content">
                <h3 className="kyc-step-title">{docTypeName} Front</h3>
                <div className="kyc-passport-instructions">
                  <p className="kyc-instruction-text">
                    Open the {docTypeName.toLowerCase()} and take a picture of the front side.
                  </p>
                  <p className="kyc-instruction-text">
                    Avoid glare and make sure all 4 corners are visible.
                  </p>
                </div>

                {showCamera ? (
                  <div className="kyc-camera-container">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="kyc-camera-video"
                    />
                    <div className="kyc-camera-controls">
                      <GenericButton
                        text="Cancel"
                        onClick={stopCamera}
                        className="kyc-camera-cancel"
                        styles={{ backgroundColor: '#6c757d', marginRight: '10px' }}
                      />
                      <GenericButton
                        text="Capture Photo"
                        onClick={capturePhoto}
                        className="kyc-camera-capture"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="kyc-passport-screen">
                      {documents.ID_FRONT.file && documents.ID_FRONT.uploadStatus === 'success' ? (
                        <img
                          src={URL.createObjectURL(documents.ID_FRONT.file)}
                          alt="Front"
                          className="kyc-passport-preview"
                        />
                      ) : (
                        <div className="kyc-passport-placeholder">
                          <p>Front preview will appear here</p>
                        </div>
                      )}
                    </div>
                    <div className="kyc-passport-actions">
                      <GenericButton
                        text="Launch Camera"
                        onClick={() => launchCamera('ID_FRONT')}
                        className="kyc-camera-button"
                      />
                      <GenericButton
                        text="Upload File"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e: any) => {
                            const file = e.target.files?.[0] || null;
                            if (file) handleFileChange('ID_FRONT', file);
                          };
                          input.click();
                        }}
                        className="kyc-not-now-button"
                      />
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handleFileChange('ID_FRONT', file);
                      }}
                      className="kyc-file-input"
                      id="file-ID_FRONT"
                      style={{ display: 'none' }}
                    />
                  </>
                )}
              </div>
            );
          }

          // Show Back capture screen
          if (hasFront && !hasBack) {
            return (
              <div className="kyc-step-content">
                <h3 className="kyc-step-title">{docTypeName} Back</h3>
                <div className="kyc-passport-instructions">
                  <p className="kyc-instruction-text">
                    Now take a picture of the back side of your {docTypeName.toLowerCase()}.
                  </p>
                  <p className="kyc-instruction-text">
                    Avoid glare and make sure all 4 corners are visible.
                  </p>
                </div>

                {showCamera ? (
                  <div className="kyc-camera-container">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="kyc-camera-video"
                    />
                    <div className="kyc-camera-controls">
                      <GenericButton
                        text="Cancel"
                        onClick={stopCamera}
                        className="kyc-camera-cancel"
                        styles={{ backgroundColor: '#6c757d', marginRight: '10px' }}
                      />
                      <GenericButton
                        text="Capture Photo"
                        onClick={capturePhoto}
                        className="kyc-camera-capture"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="kyc-passport-screen">
                      {documents.ID_BACK.file && documents.ID_BACK.uploadStatus === 'success' ? (
                        <img
                          src={URL.createObjectURL(documents.ID_BACK.file)}
                          alt="Back"
                          className="kyc-passport-preview"
                        />
                      ) : (
                        <div className="kyc-passport-placeholder">
                          <p>Back preview will appear here</p>
                        </div>
                      )}
                    </div>
                    <div className="kyc-passport-actions">
                      <GenericButton
                        text="Launch Camera"
                        onClick={() => launchCamera('ID_BACK')}
                        className="kyc-camera-button"
                      />
                      <GenericButton
                        text="Upload File"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e: any) => {
                            const file = e.target.files?.[0] || null;
                            if (file) handleFileChange('ID_BACK', file);
                          };
                          input.click();
                        }}
                        className="kyc-not-now-button"
                      />
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handleFileChange('ID_BACK', file);
                      }}
                      className="kyc-file-input"
                      id="file-ID_BACK"
                      style={{ display: 'none' }}
                    />
                  </>
                )}
              </div>
            );
          }

          // Show Selfie capture screen
          if (hasFront && hasBack && !hasSelfie) {
            return (
              <div className="kyc-step-content">
                <h3 className="kyc-step-title">Selfie</h3>
                <div className="kyc-passport-instructions">
                  <p className="kyc-instruction-text">
                    Take a selfie to verify your identity.
                  </p>
                  <p className="kyc-instruction-text">
                    Make sure your face is clearly visible and well-lit.
                  </p>
                </div>

                {showCamera ? (
                  <div className="kyc-camera-container">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="kyc-camera-video"
                    />
                    <div className="kyc-camera-controls">
                      <GenericButton
                        text="Cancel"
                        onClick={stopCamera}
                        className="kyc-camera-cancel"
                        styles={{ backgroundColor: '#6c757d', marginRight: '10px' }}
                      />
                      <GenericButton
                        text="Capture Photo"
                        onClick={capturePhoto}
                        className="kyc-camera-capture"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="kyc-passport-screen">
                      {documents.SELFIE.file && documents.SELFIE.uploadStatus === 'success' ? (
                        <img
                          src={URL.createObjectURL(documents.SELFIE.file)}
                          alt="Selfie"
                          className="kyc-passport-preview"
                        />
                      ) : (
                        <div className="kyc-passport-placeholder">
                          <p>Selfie preview will appear here</p>
                        </div>
                      )}
                    </div>
                    <div className="kyc-passport-actions">
                      <GenericButton
                        text="Launch Camera"
                        onClick={() => launchCamera('SELFIE')}
                        className="kyc-camera-button"
                      />
                      {/* <GenericButton
                        text="Not Now"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e: any) => {
                            const file = e.target.files?.[0] || null;
                            if (file) handleFileChange('SELFIE', file);
                          };
                          input.click();
                        }}
                        className="kyc-not-now-button"
                      /> */}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handleFileChange('SELFIE', file);
                      }}
                      className="kyc-file-input"
                      id="file-SELFIE"
                      style={{ display: 'none' }}
                    />
                  </>
                )}
              </div>
            );
          }

          // All documents uploaded - should auto-advance, but show message if still here
          return (
            <div className="kyc-step-content">
              <p>All required documents uploaded. Redirecting to review...</p>
            </div>
          );
        }

        // Generic fallback (should not reach here)
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">Document Upload</h3>
            <p className="kyc-step-subtitle">
              {selectedDocumentType === 'PASSPORT'
                ? 'Please upload your passport'
                : 'Please upload your documents'}
            </p>
          </div>
        );

      case 5:
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">Review & Submit</h3>
            <div className="kyc-review-section">
              <div className="kyc-review-item">
                <h4>Uploaded Documents</h4>
                <div className="kyc-review-images">
                  {(() => {
                    // Get passport document from API or local state
                    const passportDoc = fetchedDocuments.find(d =>
                      d.type === 'passport' && (!d.side || d.side === 'front')
                    );
                    const passportUrl = passportDoc?.url || imageUrls.PASSPORT ||
                      (documents.PASSPORT.file ? URL.createObjectURL(documents.PASSPORT.file) : '');

                    // Get selfie document from API or local state
                    const selfieDoc = fetchedDocuments.find(d => d.type === 'SELFIE');
                    const selfieUrl = selfieDoc?.url || imageUrls.SELFIE ||
                      (documents.SELFIE.file ? URL.createObjectURL(documents.SELFIE.file) : '');

                    // Get ID front document from API or local state
                    const idFrontDoc = fetchedDocuments.find(d =>
                      (d.type === 'national_id' || d.type === 'driving_licence') &&
                      (!d.side || d.side === 'front')
                    );
                    const idFrontUrl = idFrontDoc?.url || imageUrls.ID_FRONT ||
                      (documents.ID_FRONT.file ? URL.createObjectURL(documents.ID_FRONT.file) : '');

                    // Get ID back document from API or local state
                    const idBackDoc = fetchedDocuments.find(d =>
                      (d.type === 'national_id' || d.type === 'driving_licence') &&
                      d.side === 'back'
                    );
                    const idBackUrl = idBackDoc?.url || imageUrls.ID_BACK ||
                      (documents.ID_BACK.file ? URL.createObjectURL(documents.ID_BACK.file) : '');

                    return (
                      <>
                        {selectedDocumentType === 'PASSPORT' && (passportUrl || documents.PASSPORT.uploadStatus === 'success') && (
                          <div className="kyc-review-image-item">
                            <h5>Passport</h5>
                            <div className="kyc-review-image-container">
                              {passportUrl && (
                                <img
                                  src={passportUrl}
                                  alt="Passport"
                                  className="kyc-review-image"
                                  onError={(e) => {
                                    console.error('Failed to load passport image');
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        {(selfieUrl || documents.SELFIE.uploadStatus === 'success') && (
                          <div className="kyc-review-image-item">
                            <h5>Selfie</h5>
                            <div className="kyc-review-image-container">
                              {selfieUrl && (
                                <img
                                  src={selfieUrl}
                                  alt="Selfie"
                                  className="kyc-review-image"
                                  onError={(e) => {
                                    console.error('Failed to load selfie image');
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        {selectedDocumentType === 'NATIONAL_ID' && (idFrontUrl || documents.ID_FRONT.uploadStatus === 'success') && (
                          <div className="kyc-review-image-item">
                            <h5>National ID Front</h5>
                            <div className="kyc-review-image-container">
                              {idFrontUrl && (
                                <img
                                  src={idFrontUrl}
                                  alt="National ID Front"
                                  className="kyc-review-image"
                                  onError={(e) => {
                                    console.error('Failed to load National ID Front image');
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        {selectedDocumentType === 'NATIONAL_ID' && (idBackUrl || documents.ID_BACK.uploadStatus === 'success') && (
                          <div className="kyc-review-image-item">
                            <h5>National ID Back</h5>
                            <div className="kyc-review-image-container">
                              {idBackUrl && (
                                <img
                                  src={idBackUrl}
                                  alt="National ID Back"
                                  className="kyc-review-image"
                                  onError={(e) => {
                                    console.error('Failed to load National ID Back image');
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        {selectedDocumentType === 'DRIVING_LICENSE' && (idFrontUrl || documents.ID_FRONT.uploadStatus === 'success') && (
                          <div className="kyc-review-image-item">
                            <h5>Driving License Front</h5>
                            <div className="kyc-review-image-container">
                              {idFrontUrl && (
                                <img
                                  src={idFrontUrl}
                                  alt="Driving License Front"
                                  className="kyc-review-image"
                                  onError={(e) => {
                                    console.error('Failed to load Driving License Front image');
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        {selectedDocumentType === 'DRIVING_LICENSE' && (idBackUrl || documents.ID_BACK.uploadStatus === 'success') && (
                          <div className="kyc-review-image-item">
                            <h5>Driving License Back</h5>
                            <div className="kyc-review-image-container">
                              {idBackUrl && (
                                <img
                                  src={idBackUrl}
                                  alt="Driving License Back"
                                  className="kyc-review-image"
                                  onError={(e) => {
                                    console.error('Failed to load Driving License Back image');
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="kyc-warning-box">
              <p>⚠️ Please confirm all data is correct. Once submitted, changes may be limited.</p>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="kyc-step-content">
            <h3 className="kyc-step-title">Verification Status</h3>
            {kycStatus && (
              <div className="kyc-status-container">
                <div className="kyc-status-badge">
                  {kycStatus.status
                    ? kycStatus.status.replace(/_/g, ' ').toUpperCase()
                    : 'STATUS'}
                </div>
                {(kycStatus.status === 'pending' || kycStatus.status === 'under_review' || !kycStatus.status) && (
                  <div className="kyc-status-message">
                    <h4>Application Under Review</h4>
                    <p>Your KYC application is currently under review. Please wait for approval.</p>
                  </div>
                )}
                {kycStatus.status === 'rejected' && (
                  <div className="kyc-rejection-reason">
                    <p className="kyc-rejection-title">Rejection Reason:</p>
                    <p className="kyc-rejection-text">
                      {typeof kycStatus.rejectionReason === 'string' &&
                        kycStatus.rejectionReason.trim() !== '' &&
                        kycStatus.rejectionReason.trim().toLowerCase() !== 'none'
                        ? kycStatus.rejectionReason
                        : 'No specific reason provided.'}
                    </p>
                    <p className="kyc-reapply-message">
                      You can resubmit your KYC application immediately.
                    </p>
                    <GenericButton text="Resubmit KYC" onClick={handleReapply} />
                  </div>
                )}
                {kycStatus.status === 'need_more_info' && (
                  <div className="kyc-need-more-info">
                    <p>Additional information is required. Please review and update your application.</p>
                    <GenericButton
                      text="Edit Application"
                      onClick={() => setActiveStep(0)}
                    />
                  </div>
                )}
                {kycStatus.status === 'approved' && (
                  <div className="kyc-approved">
                    <p>✓ Your verification has been approved!</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="kyc-verification-container">
      <div className="kyc-header">
        <h1 className="kyc-main-title">Advanced Verification</h1>
        <p className="kyc-subtitle">Unlock further features and raise your deposit and withdrawal limits</p>
      </div>

      <Box className="kyc-stepper-container">
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Card className="kyc-card">
        <Card.Body className="kyc-card-body">
          {renderStepContent()}
          {renderKycDocumentManagementPanel()}

          {activeStep === 1 && (
            <div className="kyc-actions kyc-actions-left">
              <GenericButton
                text="Back"
                onClick={handleBack}
                className="kyc-back-button kyc-back-button-outline"
              />
            </div>
          )}
          {activeStep > 1 && activeStep < 6 && activeStep !== 2 && (
            <div className="kyc-actions kyc-actions-with-back">
              {activeStep > 2 && (
                <GenericButton
                  text="Back"
                  onClick={handleBack}
                  className="kyc-back-button kyc-back-button-outline"
                />
              )}
              {activeStep === 3 && (
                <GenericButton
                  text="Next"
                  onClick={handleNext}
                  loading={loading}
                  disabled={!selectedCountry}
                  className="kyc-next-button"
                />
              )}
              {activeStep === 5 && (
                <GenericButton
                  text="Submit for Review"
                  onClick={handleNext}
                  loading={loading}
                  className="kyc-next-button"
                />
              )}
            </div>
          )}
          {activeStep === 6 && (
            <div className="kyc-actions kyc-actions-left">
              <GenericButton
                text="Back to Account"
                onClick={() => navigate('/indexx-exchange/account')}
                className="kyc-back-button"
              />
            </div>
          )}
        </Card.Body>
      </Card>
      {showLoginPopup && <LoginRequiredPopup onClose={handleLoginPopupClose} />}
    </div>
  );
};

export default VerifyWithIndexx;
